import type {
	AgeInterval,
	CreateParameterFormData,
} from "../parameter/parameter";
import type { CreateInsuranceFormData } from "./insurance";

/**
 * Raw shape returned by `GET /v1/admin/insurance-product-definitions`.
 *
 * The backend is the source of truth for the catalog of available insurance
 * products and their default parameter values. This is the entry-level shape
 * before being mapped into a `CreateInsuranceFormData` template.
 */
export interface InsuranceProductDefinitionRaw {
	id: string;
	label: string;
	insurance_type: CreateInsuranceFormData["type"];
	default_parameters: ProductDefinitionParameterRaw[];
	created_at: string;
}

/**
 * A single parameter as returned by the backend.
 *
 * `value` may be:
 * - a string (most numeric / age params, encoded as strings),
 * - a boolean (toggle params),
 * - an array of interval objects (currently only `intervals`),
 * - or absent entirely (no default).
 */
export interface ProductDefinitionParameterRaw {
	type: string;
	value?:
		| string
		| boolean
		| ReadonlyArray<{ minAge: string; maxAge: string; value: string }>;
}

/**
 * Parameter types whose UI value is a boolean. When the API omits a value for
 * one of these we default to `false` (matching the previous local defaults).
 */
const BOOLEAN_PARAMETER_TYPES = new Set<string>([
	"min-invalidity",
	"dependents-supplement",
	"fixed-dependents-supplement",
	"child-supplement",
	"child-supplement-coordination",
	"funeral-allowance",
	"fixed-dependents-allowance",
]);

const isIntervalArray = (
	value: ProductDefinitionParameterRaw["value"],
): value is ReadonlyArray<{
	minAge: string;
	maxAge: string;
	value: string;
}> => Array.isArray(value);

const mapIntervals = (
	raw: ReadonlyArray<{ minAge: string; maxAge: string; value: string }>,
): AgeInterval[] =>
	raw.map((interval) => ({
		id: crypto.randomUUID(),
		minAge: interval.minAge ?? "",
		maxAge: interval.maxAge ?? "",
		value: interval.value ?? "",
	}));

const productDefinitionParameterToFormData = (
	raw: ProductDefinitionParameterRaw,
): CreateParameterFormData => {
	const id = crypto.randomUUID();

	if (raw.type === "intervals") {
		const value = isIntervalArray(raw.value) ? mapIntervals(raw.value) : [];
		return {
			id,
			type: "intervals",
			value,
			isAltered: false,
			error: null,
		};
	}

	if (typeof raw.value === "boolean" || BOOLEAN_PARAMETER_TYPES.has(raw.type)) {
		const value = typeof raw.value === "boolean" ? raw.value : false;
		// Cast through `unknown` because the discriminated union ties each
		// `type` literal to a specific value type. The runtime `type` string
		// is trusted to match the API contract.
		return {
			id,
			type: raw.type,
			value,
			isAltered: false,
			error: null,
		} as unknown as CreateParameterFormData;
	}

	const value = typeof raw.value === "string" ? raw.value : "";
	return {
		id,
		type: raw.type,
		value,
		isAltered: false,
		error: null,
	} as unknown as CreateParameterFormData;
};

/**
 * Convert a single API product definition into a `CreateInsuranceFormData`
 * template suitable for seeding the create-account form.
 *
 * Every template starts as a fresh, non-removable, unselected row in
 * "create" mode. The page-level form layer is responsible for mutating
 * provider, groups, parameter values, etc. afterwards.
 */
export const productDefinitionToTemplate = (
	raw: InsuranceProductDefinitionRaw,
): CreateInsuranceFormData => ({
	id: crypto.randomUUID(),
	type: raw.insurance_type,
	provider: "",
	mode: "create",
	groups: [],
	error: null,
	isRemovable: false,
	parameters: (raw.default_parameters ?? [])
		.filter((param): param is ProductDefinitionParameterRaw =>
			Boolean(param && typeof param.type === "string"),
		)
		.map(productDefinitionParameterToFormData),
});

/**
 * Map an entire API payload to an ordered list of templates. The API order
 * is preserved and used as the canonical display order downstream.
 */
export const productDefinitionsToTemplates = (
	raws: ReadonlyArray<InsuranceProductDefinitionRaw>,
): CreateInsuranceFormData[] => raws.map(productDefinitionToTemplate);
